/**
 * BanNotice - what somebody sees instead of their account
 * -------------------------------------------------------
 * Shown on the login screen when the password was RIGHT and the account is
 * banned. That ordering matters and is enforced on the server: a ban is only
 * ever revealed to somebody who has proved the account is theirs, otherwise
 * the login form becomes a way of finding out who has been banned.
 *
 * Three things it has to do, and they pull in different directions:
 *
 *   1. Say plainly that they cannot get in, without being cruel about it.
 *   2. Give the moderator's reason WORD FOR WORD. Whatever was typed is what
 *      they see. A summary or a softened version would leave them arguing
 *      with a sentence nobody wrote.
 *   3. Say exactly how long is left, and keep saying it. A date alone makes
 *      somebody do arithmetic; "3 days, 4 hours, 12 minutes" does not.
 *
 * THE CLOCK TICKS EVERY SECOND, though it only shows down to minutes. That is
 * deliberate: a countdown that jumps a whole minute at a time looks frozen,
 * and somebody staring at a screen that will not let them in is exactly the
 * person who notices. The seconds do the work of making it feel alive; the
 * minutes are what gets read.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, Clock } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

/**
 * Days / hours / minutes still to run, or null once it is over.
 * Returns whole units only - nobody needs the seconds spelled out, and a
 * number that changes under your eyes is harder to read, not easier.
 */
function timeLeft(until) {
  const ms = new Date(until).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;

  return {
    days: Math.floor(ms / 86400000),
    hours: Math.floor((ms % 86400000) / 3600000),
    minutes: Math.floor((ms % 3600000) / 60000),
  };
}

/**
 * "3 days, 4 hours and 12 minutes", skipping anything that is zero.
 *
 * Takes t rather than writing the words itself. It used to build them in
 * English, so an Italian reader saw an Italian heading sitting above an
 * English countdown - and check-translations could not see it, because
 * these were never phrases in the dictionary to be missing.
 *
 * The joining words are looked up too: Italian joins with " e ", and a
 * hard-coded " and " would have survived every other fix.
 */
function spell(left, t) {
  const parts = [];
  if (left.days) parts.push(t('ban.days', { count: left.days }));
  if (left.hours) parts.push(t('ban.hours', { count: left.hours }));
  if (left.minutes) parts.push(t('ban.minutes', { count: left.minutes }));

  // Under a minute left. "0 minutes" reads as broken; this reads as nearly over.
  if (!parts.length) return t('ban.lessThanMinute');

  if (parts.length === 1) return parts[0];
  return parts.slice(0, -1).join(t('ban.listJoin')) + t('ban.and') + parts[parts.length - 1];
}

export default function BanNotice({ ban, onFinished }) {
  const { t } = useI18n();
  const [left, setLeft] = useState(() => (ban.permanent ? null : timeLeft(ban.until)));

  useEffect(() => {
    if (ban.permanent) return undefined;

    const tick = setInterval(() => {
      const next = timeLeft(ban.until);
      setLeft(next);
      // Ran out while they were sitting here. Tell the page, so the login
      // form comes back without them having to work out that reloading might
      // help.
      if (!next && typeof onFinished === 'function') onFinished();
    }, 1000);

    return () => clearInterval(tick);
  }, [ban.permanent, ban.until, onFinished]);

  const over = !ban.permanent && !left;

  // --color-error and .text-error below, not the --color-red and .text-red
  // this used to name: neither of those exists, so the border and the icon
  // were silently the default grey. Found 16 September 2026.
  return (
    <div className="card" style={{ borderColor: 'var(--color-error)', borderWidth: 2 }}>
      <div className="row" style={{ gap: 'var(--space-xs)', alignItems: 'flex-start' }}>
        <ShieldAlert size={24} className="text-error" style={{ flexShrink: 0 }} aria-hidden="true" />

        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ marginBottom: 6 }}>
            {ban.permanent ? t('ban.closedTitle') : t('ban.suspendedTitle')}
          </h2>

          {/* The moderator's own words, exactly as typed. */}
          <p className="text-muted" style={{ marginBottom: 'var(--space-sm)' }}>
            {ban.reason}
          </p>

          {ban.permanent ? (
            <p className="text-muted">
              {t('ban.permanentBody')} <strong>safety@choreconnect.org</strong>.
            </p>
          ) : over ? (
            <div className="alert alert-success">
              <Clock size={18} aria-hidden="true" />
              <div>{t('ban.over')}</div>
            </div>
          ) : (
            <>
              {/* aria-live so a screen reader announces the countdown rather
                  than leaving somebody with no idea how long is left. It is
                  'polite', not 'assertive' - it should not interrupt every
                  minute. */}
              <div
                className="alert alert-warning"
                aria-live="polite"
                style={{ marginBottom: 'var(--space-sm)' }}
              >
                <Clock size={18} aria-hidden="true" />
                <div>
                  <strong>{t('ban.timeLeft', { time: spell(left, t) })}</strong>
                </div>
              </div>
              <p className="hint">
                {t('ban.canLogInAfter')} {t('ban.mistakeBody')}{' '}
                <strong>safety@choreconnect.org</strong>.
              </p>
            </>
          )}

          <div style={{ marginTop: 'var(--space-md)' }}>
            <Link to="/" className="btn btn-outline btn-block">
              {t('ban.backHome')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
